import { useCallback, useEffect, useRef, type RefObject } from "react";

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** Re-arms the observer when this changes (e.g. the rendered item count). */
  resetKey?: unknown;
  /** The scrollport the sentinel lives in, when it is not the viewport. */
  root?: RefObject<Element | null>;
  rootMargin?: string;
}

/**
 * Calls `loadMore` when the returned sentinel scrolls into view, guarded by `hasMore` and
 * `isLoadingMore`. Attach the returned callback ref to a trailing element after the list.
 */
export function useInfiniteScroll<T extends HTMLElement = HTMLElement>({
  hasMore,
  isLoadingMore,
  loadMore,
  resetKey,
  root,
  rootMargin = "200px",
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Live refs so the observer callback never reads a stale guard or closure.
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingMoreRef = useRef(isLoadingMore);
  isLoadingMoreRef.current = isLoadingMore;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const observeSentinel = useCallback(() => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMoreRef.current && !isLoadingMoreRef.current) {
          loadMoreRef.current();
        }
      },
      { root: root?.current ?? null, rootMargin },
    );

    observer.observe(el);
    observerRef.current = observer;
  }, [root, rootMargin]);

  // A callback ref rather than a ref object: a list that unmounts its sentinel and later renders a
  // new one (opening a detail view and coming back) swaps the node without changing `resetKey`, and
  // an effect keyed on `resetKey` alone would keep observing the detached node.
  const setSentinel = useCallback(
    (node: T | null) => {
      sentinelRef.current = node;
      observeSentinel();
    },
    [observeSentinel],
  );

  // Re-arm on `resetKey` too: after a page loads, the sentinel can still be intersecting, and
  // IntersectionObserver only reports transitions.
  useEffect(() => {
    observeSentinel();
    return () => observerRef.current?.disconnect();
  }, [observeSentinel, resetKey]);

  return setSentinel;
}
